import { useIntl } from '@/intl'
import { CollectionId } from '@/scopes/common/components/CollectionCard/types'
import {
  getCollectionIcon,
  getCollectionNameId,
} from '@/scopes/common/components/CollectionCard/utils'
import { MediaServerImage } from '@/scopes/common/components/MediaServerImage'
import { useConfiguration } from '@/scopes/common/infrastructure/config/ConfigProvider'
import { Icon } from '@sunwebgroup/uds-ui/icon'
import { RichText } from '@sunwebgroup/uds-ui/rich-text'
import { Typography } from '@sunwebgroup/uds-ui/typography'
import cn from 'classnames'
import styles from './CollectionCard.module.css'

interface CollectionProps {
  imageUrl?: string
  collectionId: CollectionId
  name: string
  title: string
  description: string
  usps: string[]
}

const COLLECTION_CLASS: Record<CollectionId, string> = {
  'atlantis': styles.collectionAtlantis,
  'boutique': styles.collectionBoutique,
  'cycling': styles.collectionCycling,
  'excellent': styles.collectionExcellent,
  'famfun': styles.collectionFamfun,
  'primavera': styles.collectionPrimavera,
  'secrets': styles.collectionSecrets,
  'selections': styles.collectionSelections,
  'totally_snow': styles.collectionTotallySnow,
  'villages': styles.collectionVillages,
  'xperience': styles.collectionXperience,
  'child': styles.collectionChild,
  'pearls': styles.collectionPearls,
  'culinary': styles.collectionCulinary,
  'romantic': styles.collectionRomantic,
  'newest-discoveries': styles.collectionNewestDiscoveries,
}

export function CollectionCard({ imageUrl, collectionId, title, name, description, usps }: CollectionProps) {
  const brandName = useConfiguration('brandName')
  const intl = useIntl()

  const collectionClass = COLLECTION_CLASS[collectionId] ?? styles.collectionDefault

  return (
    <div className={cn(styles.wrapper, collectionClass)}>
      {imageUrl && (
        <div className={styles.imageWrapper}>
          <MediaServerImage src={imageUrl} alt={name} widths={[]} />
          <div className={cn(styles.badge, collectionClass)}>
            <Icon glyph={getCollectionIcon(collectionId, brandName)} size={32} />
            <Typography variant="body-xs-bold-flat">
              {name || intl.formatMessage({ id: getCollectionNameId(collectionId) })}
            </Typography>
          </div>
        </div>
      )}
      <div className={styles.content}>
        <Typography className={styles.label} variant="display-f-regular">
          {title}
        </Typography>
        <RichText className={cn(styles.description, collectionClass)} innerHTML={description} />
        <ul className={styles.uspsWrapper}>
          {usps.map(item => (
            <li key={item.toLowerCase().replace(' ', '-')} className={styles.uspItem}>
              <Icon glyph="CheckMarkSmall" size={16} />
              <Typography variant="body-m-regular-tight">{item}</Typography>
            </li>
          ))}
        </ul>
      </div>
    </div>
  )
}
